import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SnackbarService } from '../utils/snackbar.service';
import { interval, Subscription, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

interface AppVersion {
  buildTimestamp: string;
}

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_DELAY_MS = 60 * 1000;

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private readonly http = inject(HttpClient);
  private readonly snackbarService = inject(SnackbarService);
  private currentVersion: string | null = null;
  private pollSub: Subscription | null = null;

  init(): void {
    this.fetchVersion().subscribe(version => {
      if (version) {
        this.currentVersion = version.buildTimestamp;
        this.startPolling();
      }
    });
  }

  destroy(): void {
    this.pollSub?.unsubscribe();
  }

  private fetchVersion() {
    return this.http
      .get<AppVersion>(`assets/version.json?t=${Date.now()}`)
      .pipe(
        catchError(err => {
          console.warn('[VersionCheck] Failed to fetch version.json', err);
          return of(null);
        })
      );
  }

  private startPolling(): void {
    this.pollSub = interval(POLL_INTERVAL_MS)
      .pipe(switchMap(() => this.fetchVersion()))
      .subscribe(version => {
        if (!version || !this.currentVersion) return;
        if (version.buildTimestamp !== this.currentVersion) {
          this.pollSub?.unsubscribe();
          this.notifyAndReload();
        }
      });
  }

  private notifyAndReload(): void {
    const snackRef = this.snackbarService.withAction(
      'Eine neue Version ist verfügbar. Seite wird in 60 Sekunden neu geladen…',
      'Jetzt neu laden',
      RELOAD_DELAY_MS,
      ['version-snackbar']
    );

    snackRef.onAction().subscribe(() => window.location.reload());

    snackRef.afterDismissed().subscribe(({ dismissedByAction }) => {
      if (!dismissedByAction) window.location.reload();
    });
  }
}
